import React from 'react';
import MuiTabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Box from '@mui/material/Box';
import { Link } from 'react-router-dom';
import { styled } from 'styled-components';
import { useTheme } from '@mui/material/styles';

/**
 * Defines the shape of the panel objects used to create tabs.
 */
interface Panel {
	key: string; // A unique key to identify the panel, used for finding the active tab.
	label: any; // The text displayed on the tab.
	href?: string; // The route the tab links to.
	component: React.ReactNode; // The content to render when the tab is active.
	onclick?: () => void; // Optional click handler.
}

/**
 * Props for the main Tabs component.
 */
interface TabsProps {
	panels: Panel[];
	currentPath: string;
	boxSx?: any;
	/**
	 * The visual style of the tabs.
	 * 'standard' - Default MUI style with an underline indicator.
	 * 'pill' - A rounded, button-like style.
	 * @default 'standard'
	 */
	variant?: 'standard' | 'pill';
}

/**
 * A reusable Tabs component with two visual variants.
 * It uses React Router for navigation and displays content in associated panels.
 */
export default function Tabs({
	panels,
	currentPath,
	boxSx,
	variant = 'standard',
}: TabsProps) {
	const theme = useTheme();
	// Find the index of the active tab by matching the start of the current path with the panel's key.
	const activeTab = panels.findIndex((panel) =>
		currentPath.startsWith(panel.key)
	);

	const isPillVariant = variant === 'pill';

	return (
		<>
			<Box
				sx={{
					// Only apply a bottom border for the 'standard' variant.
					borderBottom: isPillVariant ? 0 : 1,
					borderColor: 'divider',
					...boxSx,
				}}
			>
				<MuiTabs
					value={activeTab === -1 ? false : activeTab} // Handle case where no tab is selected
					variant="scrollable"
					scrollButtons="auto"
					aria-label="navigation tabs"
					sx={{
						// For the 'pill' variant, hide the default underline indicator.
						...(isPillVariant && {
							'& .MuiTabs-indicator': {
								display: 'none',
							},
						}),
					}}
				>
					{panels.map((panel, i) => (
						// @ts-ignore
						<Tab
							key={i}
							label={panel.label}
							component={Link}
							to={panel.href}
							value={i}
							onClick={panel.onclick}
							sx={{
								textTransform: 'none',
								fontSize: '0.9rem',
								// Apply specific styles for the 'pill' variant.
								...(isPillVariant && {
									minHeight: '36px',
									padding: '6px 16px',
									margin: '6px 4px',
									borderRadius: '99px', // Creates the pill shape
									backgroundColor:
										theme.palette.background.paper,
									color: theme.palette.text.primary,
									'&:hover': {
										backgroundColor:
											theme.palette.background.paper,
										opacity: 0.85,
									},
									// Style for the currently selected pill
									'&.Mui-selected': {
										backgroundColor:
											theme.palette.primary.main,
										color: theme.palette.primary
											.contrastText,
										fontWeight: 600,
									},
								}),
								// Apply styles for the 'standard' variant
								...(!isPillVariant && {
									color: theme.palette.text.primary,
									'&.Mui-selected': {
										color: theme.palette.primary.main,
										fontWeight: 600,
									},
								}),
							}}
							{...a11yProps(i)}
						/>
					))}
				</MuiTabs>
			</Box>
			{/* Render the content for the active tab panel */}
			{panels.map((panel, i) => (
				<TabPanel key={i} value={i} activeIndex={activeTab}>
					{panel.component}
				</TabPanel>
			))}
		</>
	);
}

/**
 * Generates accessibility props for a Tab.
 */
function a11yProps(index: number) {
	return {
		id: `simple-tab-${index}`,
		'aria-controls': `simple-tabpanel-${index}`,
	};
}

/**
 * Props for the TabPanel component.
 */
interface TabPanelProps {
	children: React.ReactNode;
	value: number;
	activeIndex: number;
}

/**
 * Renders the content of a tab panel, only showing it if it's the active one.
 */
function TabPanel({ children, value, activeIndex }: TabPanelProps) {
	return (
		<div
			role="tabpanel"
			hidden={value !== activeIndex}
			style={
				value === activeIndex
					? {
							flex: 1,
							minHeight: 0,
							overflow: 'hidden',
							display: 'flex',
							flexDirection: 'column',
					  }
					: {}
			}
		>
			{/* Only render children if the panel is active to potentially save resources */}
			{value === activeIndex && <>{children}</>}
		</div>
	);
}
